import {
  merchantHasAllowedStateRedirect,
  userIsAuthenticatedRedirect,
  userIsNotAuthenticatedRedirect,
} from 'auth'
import AppLayout from 'components/AppLayout'
import { LIVE_UPDATES_PATH } from 'constants/liveUpdates'
import { RECOVERY_AI_TERMS_PATH } from 'constants/policyDoc'
import useFlag from 'hooks/useFlag'
import Accessibility from 'public/Accessibility'
import ExclusiveDealsTerms from 'public/ExclusiveDealsTerms'
import Privacy from 'public/Privacy'
import CcpaNotice from 'public/Privacy/ccpanotice'
import CcpaOptOut from 'public/Privacy/CcpaOptOut'
import Previous from 'public/Privacy/previous'
import RecoveryAiTerms from 'public/RecoveryAiTerms'
import SettlementCredits from 'public/SettlementCredits'
import Terms from 'public/Terms'
import { ComponentType } from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'
import Games from 'scenes/Games'
import ZendeskLogout from '../components/ZendeskLogout'
import ZendeskSSO from '../components/ZendeskSSO'
import AlertBannerSettings from './AlertBannerSettings'
import Auth from './Auth'
import Billing from './Billing'
import CampaignCenter from './CampaignCenter'
import Cms from './Cms'
import { DashboardAccessRoutes } from './DashboardAccess/DashboardAccessRoutes'
import DeliveryLocationSettings from './DeliveryLocationSettings'
import GiftCards from './GiftCards'
import Home from './Home'
import InsightsLab from './InsightsLab'
import InsightsLabReport from './InsightsLab/Report'
import Integrations from './Integrations'
import LiveUpdates from './LiveUpdates'
import Locations from './Locations'
import LookerEmbed from './LookerEmbed'
import Loyalty from './Loyalty'
import MerchantPicker from './Merchants/MerchantPicker'
import CompleteInvitation from './MerchantUsers/Invitation/CompleteInvitation'
import InvitationSuccess from './MerchantUsers/Invitation/InvitationSuccess'
import OrderingManagement from './OrderingManagement'
import Points from './Points'
import PosConfigurations from './PosConfigurations'
import Profile from './Profile'
import ChangePassword from './Profile/ChangePassword'
import PromoCodes from './PromoCodes'
import RecoveryAI from './RecoveryAI'
import RedeemManager from './RedeemManager'
import Reports from './Reports'
import ResponsesRouteWrapper from './Responses/RouteWrapper'
import Segments from './Segments'
import ServiceChargeGroups from './ServiceChargeGroups'
import Settings from './Settings'
import SmartLinks from './SmartLinks'
import Unauthorized from './Status/Unauthorized'
import StoredValue from './StoredValue'
import ProviderExportWizard from './Subscribers/Export'
import SubscriberSetup from './Subscribers/Setup'
import SubscriberSetupOauth from './Subscribers/Setup/Oauth'
import UserLists from './UserLists'
import Users from './Users'
import UserSupport from './UserSupport'

/**
 * Define all of the routes in our app. Since we're using HOC wrappers for
 * the route components (`userIsAuthenticatedRedirect` etc.), these have to
 * be defined statically. Otherwise the component will be rebuilt and
 * remounted on every render and lead to subtle bugs.
 */

type WrappedComponent<P = {}> = ComponentType<P>

function withLayout<P>(Component: WrappedComponent<P>) {
  return (props: P & { children?: React.ReactNode }) => (
    <AppLayout>
      <Component {...props} />
    </AppLayout>
  )
}

function authenticatedRoute<P>(Component: WrappedComponent<P>) {
  return userIsAuthenticatedRedirect(
    merchantHasAllowedStateRedirect(withLayout(Component))
  )
}

const RedirectToCampaigns = () => <Redirect to="/thanx_campaigns" />
const RedirectToServiceCharges = () => <Redirect to="/service_charges" />
// Wrap the Zendesk SSO component outside of the Routes component so that
// its identity remains stable across renders. This prevents React Router
// from unmounting and remounting the component, which previously caused
// `useEffect` in `ZendeskSSO` to run multiple times.
const ZendeskSSORoute = userIsAuthenticatedRedirect(ZendeskSSO)

// Same stable-identity reason as ZendeskSSORoute above: the RecoveryAI
// activation wizard keeps in-memory state across :step navigations. An inline
// `authenticatedRoute(RecoveryAI)` produces a NEW component identity on every
// Routes render, so React Router remounts the whole RecoveryAI subtree on every
// navigation — resetting the wizard mid-flow (AI-114). Hoisting it pins the
// identity so the subtree survives navigation.
const AuthenticatedRecoveryAI = authenticatedRoute(RecoveryAI)

// Same stable-identity reason as AuthenticatedRecoveryAI above: `/reports`,
// `/order_feedback`, and `/order_ratings` all route to `Reports`, and an
// inline `authenticatedRoute(Reports)` at each `<Route>` produces a NEW
// component identity every `Routes` render — React Router remounts the whole
// Reports subtree (including the Unified Feedback Inbox, `FeedbackInbox`) on
// every navigation, even a same-page pagination/filter change that only
// touches the query string. Symptom: the feedback list's pagination footer
// (and everything else holding in-memory state) unmounts and remounts on
// every page/filter click instead of just re-rendering with new props.
// Hoisting pins the identity so the subtree survives navigation, exactly
// like AuthenticatedRecoveryAI.
//
// The report tab nav depends on this for two more reasons: report pages drive
// their tabs off the URL hash, so every tab click is a navigation. Without the
// hoist, any scroll position set while handling the click went out with the old
// DOM node (ScrollingTabNav's click-to-lead never stuck), and TabPane's
// `mountOnEnter` could not retain a pane across tab switches, so revisiting a
// tab re-fetched its Looker embed every time.
const AuthenticatedReports = authenticatedRoute(Reports)

// Same AI-114 remount hazard: AI-1295's Olo-webhook-setup deep link
// (?olo_webhook=setup) calls `history.replace` to strip the param right after
// opening the modal — that dispatches a Redux LOCATION_CHANGE, which re-renders
// Routes and, with an inline `authenticatedRoute(LiveUpdates)`, remounts the
// whole LiveUpdates subtree and force-closes the modal it just opened
// (useOloWebhookConnection's enabled-reset effect fires on the fresh mount).
// Hoisting pins the identity so the deep link's own `history.replace` doesn't
// remount the page out from under itself.
const AuthenticatedLiveUpdates = authenticatedRoute(LiveUpdates)

// Same stable-identity reason again: an inline `authenticatedRoute(Locations)`
// remounted Locations' own nested <Switch> on every Routes render (every
// history pop included), replaying its flag-off Redirect and overwriting a
// detail-page history entry with /locations mid-navigation.
const AuthenticatedLocations = authenticatedRoute(Locations)

// Same stable-identity reason: every LaunchDarkly flag change re-renders Routes,
// including the identify that settles after the first paint, so an inline
// authenticatedRoute(RedeemManager) remounted /rewards and its Landing fetched
// the full redeem_templates list a second time.
const AuthenticatedRedeemManager = authenticatedRoute(RedeemManager)

// Same flag-change remount: /segments/:id refetched sample_customers.
const AuthenticatedSegments = authenticatedRoute(Segments)

// Routes that don't appear in the layout container
export const Routes = () => {
  const customizableLegalDocsFlag = useFlag('mx-customizable-legal-docs', false)
  const ccpaOptOutRoute = customizableLegalDocsFlag
    ? '/ccpaoptout/:handle?'
    : '/ccpaoptout'

  return (
    <Switch>
      {/* Auth */}
      <Route path="/auth" component={userIsNotAuthenticatedRedirect(Auth)} />
      <Route exact path="/sso" component={ZendeskSSORoute} />
      <Route path="/sso/logout" component={ZendeskLogout} />

      <Route path="/choose" component={MerchantPicker} />

      {/* Legal Routes */}
      <Route path="/privacy/:handle?" component={Privacy} />
      <Route path="/privacy_v1" component={Previous} />
      <Route path="/ccpanotice" component={CcpaNotice} />
      <Route path={ccpaOptOutRoute} component={CcpaOptOut} />
      <Route path="/terms/:handle?" component={Terms} />
      <Route exact path="/settlement_credits" component={SettlementCredits} />
      <Route
        exact
        path="/exclusive-deals-terms"
        component={ExclusiveDealsTerms}
      />
      <Route exact path="/accessibility" component={Accessibility} />
      <Route exact path={RECOVERY_AI_TERMS_PATH} component={RecoveryAiTerms} />

      {/* Authenticated routes */}
      <Route exact path="/" component={authenticatedRoute(Home)} />
      <Route path="/offers" component={RedirectToCampaigns} />
      {/* Fake route to handle opening a modal on navigation */}
      <Route
        path="/thanx_campaigns_m"
        component={() => <Redirect to="/thanx_campaigns?selected=message" />}
      />
      <Route
        path="/thanx_campaigns"
        component={authenticatedRoute(CampaignCenter)}
      />
      <Route
        path="/smart_links_overview"
        component={authenticatedRoute(SmartLinks)}
      />
      <Route path="/rewards" component={AuthenticatedRedeemManager} />
      <Route path="/settings" component={authenticatedRoute(Settings)} />
      <Route path="/brand_content" component={authenticatedRoute(Cms)} />
      <Route
        path="/subscribers/initial_setup"
        render={() => <Redirect to="/subscribers/setup" />}
      />
      <Route
        path="/subscribers/setup"
        component={authenticatedRoute(SubscriberSetup)}
      />
      <Route
        path="/subscribers/export"
        component={authenticatedRoute(ProviderExportWizard)}
      />
      <Route
        path="/subscribers/oauth"
        component={authenticatedRoute(SubscriberSetupOauth)}
      />
      <Route path="/program_reviews/:id" render={() => <Redirect to="/" />} />
      <Route path="/loyalty" component={authenticatedRoute(Loyalty)} />
      <Route path="/reports" component={AuthenticatedReports} />
      {/* Canonical deep link every Orders 👍/👎 nav entry point targets. The
          scene is registered inside the Reports router, which also owns the
          nested /order_feedback/ratings/:ratingId detail route. */}
      <Route path="/order_feedback" component={AuthenticatedReports} />
      {/* The Looker report that used to live at /order_feedback — ReportsRouter
          mounts OrderRatingsReport here, but only once this parent path is
          itself registered; without it, the home feedback tile and
          satisfaction metric's links to /order_ratings 404. */}
      <Route path="/order_ratings" component={AuthenticatedReports} />
      <Route path="/points" component={authenticatedRoute(Points)} />
      <Route path="/promo_codes" component={authenticatedRoute(PromoCodes)} />
      <Route
        path="/ordering_management"
        component={authenticatedRoute(OrderingManagement)}
      />
      <Route path="/stored_value" component={authenticatedRoute(StoredValue)} />
      <Route path="/gift_cards" component={authenticatedRoute(GiftCards)} />
      <Route path="/profile" component={authenticatedRoute(Profile)} />
      <Route path="/billing" component={authenticatedRoute(Billing)} />
      <Route
        path="/change_password"
        component={authenticatedRoute(ChangePassword)}
      />
      <Route path="/invitation/success" component={InvitationSuccess} />
      <Route path="/invitation/:token" component={CompleteInvitation} />
      <Route
        path="/integrations"
        component={authenticatedRoute(Integrations)}
      />
      <Route path="/games" component={authenticatedRoute(Games)} />
      <Route path="/user_lists" component={authenticatedRoute(UserLists)} />
      <Route path="/users" component={authenticatedRoute(Users)} />
      <Route path="/segments" component={AuthenticatedSegments} />
      <Route path="/recovery_ai" component={AuthenticatedRecoveryAI} />
      <Route
        path="/responses"
        component={authenticatedRoute(ResponsesRouteWrapper)}
      />
      <Route path="/user_support" component={authenticatedRoute(UserSupport)} />
      <Route
        path="/alert_banners"
        component={authenticatedRoute(AlertBannerSettings)}
      />
      <Route path="/locations" component={AuthenticatedLocations} />
      <Route
        path="/delivery_location_settings"
        component={authenticatedRoute(DeliveryLocationSettings)}
      />
      <Route
        path="/pos_configurations"
        component={authenticatedRoute(PosConfigurations)}
      />
      <Route path={LIVE_UPDATES_PATH} component={AuthenticatedLiveUpdates} />
      <Route
        path="/service_charge_groups"
        component={RedirectToServiceCharges}
      />
      <Route
        path="/service_charges"
        component={authenticatedRoute(ServiceChargeGroups)}
      />
      <Route path="/unauthorized" component={withLayout(Unauthorized)} />
      <Route
        path="/merchant_users"
        component={authenticatedRoute(DashboardAccessRoutes)}
      />
      <Route path="/l/" component={withLayout(LookerEmbed)} strict />
      <Route
        path="/insights_lab/:id"
        component={withLayout(InsightsLabReport)}
        strict
      />
      <Route path="/insights_lab" component={withLayout(InsightsLab)} strict />
    </Switch>
  )
}
